import { environment } from 'environments/environment';
import { DossierNavigationService } from 'app/core/navigation/dossier-navigation.service';
import { hasSuperAdminRole, hasSuperAdminSettingsAccess } from 'app/shared/utils/super-admin-access.util';
import { isCommonSicaRoute, isSuperAdminOnlySicaRoute } from 'app/shared/utils/sica-route-catalog';
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { compactNavigation, futuristicNavigation } from 'app/mock-api/common/navigation/data';
import { UserService } from 'app/core/user/user.service';
import { User_UserProfile } from 'app/modules/models/profile-management/userProfile.type';
import { UserPermission } from 'app/modules/models/profile-management/userPermission.type';
import { DataService } from './data.service';

@Injectable({
    providedIn: 'root'
})
export class NavigationMockApi {

    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[];
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[];
    static userPermissions = [];
    static user_UserProfiles: User_UserProfile[] = [];

    /**
     * Constructor
     */
    constructor(private readonly _fuseMockApiService: FuseMockApiService,
        private readonly _userService: UserService,
        private readonly  dataService: DataService,
        private readonly dossierNavigation: DossierNavigationService
    ) {
        // Register Mock API handlers
      this._defaultNavigation = this.dataService.getDefaultNavigation();
      this._horizontalNavigation = this.dataService.getHorizontalNavigation();
        this.registerHandlers();
        this.getUserPermissions();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet(environment.apiCatalog.common.controllerRoute + environment.apiCatalog.common.subRoutes.navigation)
            .reply(() => {

                // Fill compact navigation children using the default navigation
                this._compactNavigation.forEach((compactNavItem) => {
                    this._defaultNavigation.forEach((defaultNavItem) => {
                        if (defaultNavItem.id === compactNavItem.id) {
                            compactNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill futuristic navigation children using the default navigation
                this._futuristicNavigation.forEach((futuristicNavItem) => {
                    this._defaultNavigation.forEach((defaultNavItem) => {
                        if (defaultNavItem.id === futuristicNavItem.id) {
                            futuristicNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });
                const defaultNavigation = this.organizeNavigation(this.filterNavigation(this._defaultNavigation));
                const compactNavigation = this.filterNavigation(this._compactNavigation);
                const futuristicNavigation = this.filterNavigation(this._futuristicNavigation);
                const horizontalNavigation = this.organizeNavigation(this.filterNavigation(this._horizontalNavigation));

                // Return only pages granted by the TERA SICA profiles.
                return [
                    200,
                    {
                        compact: compactNavigation,
                        default: defaultNavigation,
                        futuristic: futuristicNavigation,
                        horizontal: horizontalNavigation
                    }
                ];
            });
    }
    
    getUserPermissions() {
        this._userService.connectedUser$.subscribe(res => {  
            NavigationMockApi.user_UserProfiles = res?.user_UserProfiles || [];
            NavigationMockApi.userPermissions = this.getPermissions(res?.user_UserProfiles || []);
        });
    }

    private getPermissions(userUserProfiles: User_UserProfile[]): UserPermission[] {
        const permissionMap = new Map<string, UserPermission>();
    
        (userUserProfiles || []).forEach(p => {
          if (p.userProfile?.permissions) {
            p.userProfile.permissions.forEach(permission => {
              const permissionKey = permission.pageRoute || permission.id || permission.name;
              if (!permissionMap.has(permissionKey)) {
                permissionMap.set(permissionKey, permission);
              }
            });
          }
        });
    
        return Array.from(permissionMap.values());
      }

    private filterNavigation(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return (items || []).reduce((result: FuseNavigationItem[], sourceItem) => {
            const item = cloneDeep(sourceItem);
            if (item.link?.toLowerCase() == '/partners/drop-list') item.title = 'Dossiers';
            if (item.link?.toLowerCase() == '/partner-dossier-project') item.title = 'Dossiers';
            if (item.children?.length) {
                item.children = this.filterNavigation(item.children);
            }

            const hasAllowedChildren = !!item.children?.length;
            const hasAllowedLink = isSuperAdminOnlySicaRoute(item.link) ? hasSuperAdminRole() : hasSuperAdminSettingsAccess(item.link) || isCommonSicaRoute(item.link) || !!item.link && NavigationMockApi.userPermissions.some(
                permission => this.routeMatches(permission.pageRoute || '', item.link)
            );

            if ((hasAllowedChildren || hasAllowedLink) && (this.dossierNavigation?.allowsLink(item.link) ?? true)) {
                result.push(item);
            }
            return result;
        }, []);
    }

    private routeMatches(permissionRoute: string, navigationRoute: string): boolean {
        return !!permissionRoute && !!navigationRoute &&
            permissionRoute.toLowerCase() == navigationRoute.toLowerCase();
    }

    private organizeNavigation(items: FuseNavigationItem[]): FuseNavigationItem[] {
        const links: FuseNavigationItem[] = [];
        const seen = new Set<string>();
        const collect = (entries: FuseNavigationItem[], parentId = '') => entries.forEach(item => {
            if (item.children?.length) {
                collect(item.children, parentId + item.id + '-');
            } else if (item.link && !seen.has(item.link.toLowerCase())) {
                seen.add(item.link.toLowerCase());
                links.push({ ...item, id: parentId + item.id });
            }
        });
        collect(items);
        if (seen.has('/partners/drop-list') || seen.has('/partner-dossier-project')) {
            const labelDossiers = (entries: FuseNavigationItem[]) => entries.forEach(item => {
                if (item.link?.toLowerCase() == '/partners/drop-list') {
                    item.title = 'Dossiers';
                }
                if (item.link?.toLowerCase() == '/partner-dossier-project') {
                    item.title = seen.has('/partners/drop-list') ? 'Dossier projets' : 'Dossiers';
                }
                if (item.children?.length) {
                    labelDossiers(item.children);
                }
            });
            labelDossiers(items);
            labelDossiers(links);
        }
        return links.length > 7 ? items : links;
    }

    static executeActionBeforeLeavingCustomerDetails(uri) {
        // const currentRoute = NavigationService.getInstance().getCurrentRoute();
        // if(!currentRoute.includes("/prospects/customer-details")) {
        //     NavigationService.getInstance().navigateToUri(uri);
        // } else {
        //     NavigationService.getInstance().addProspect = {type: 'create', uri: uri, value: true};
        // }
    }
}
